import { InvalidNightConfigError } from '../errors'
import { parseHhmmToMinutes } from './_internal/parseHhmm'
import { durationAcrossMidnight } from './crossMidnight'

const MINUTES_PER_HOUR = 60
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR
const MAX_HOUR = 23

/**
 * Definition of the night period for a given tenant/rule set.
 *
 * - `startHour` is the first hour counted as night (inclusive).
 * - `endHour` is the first hour counted as day again (exclusive).
 * - The range must always cross midnight, i.e. `startHour > endHour`.
 *
 * Example — STPP default: `{ startHour: 22, endHour: 6 }` means every
 * minute whose hour is 22, 23, 0, 1, 2, 3, 4 or 5 is a night minute.
 */
export type NightConfig = {
  startHour: number
  endHour: number
}

/**
 * Default V2 night window: 22:00 → 06:00.
 *
 * Why 6 and not 5: the PHP V1 formulaire.php uses `h < 5`, which is a
 * known bug — the business spec and the V1 admin.php billing function
 * both use `h < 6`. V2 aligns with the spec, not with the accidental
 * behaviour of the legacy form.
 */
export const DEFAULT_NIGHT_CONFIG: NightConfig = {
  startHour: 22,
  endHour: 6,
}

/**
 * Counts the number of night minutes inside the slot `[start, end[`,
 * using a configurable night window (defaults to 22:00 → 06:00).
 *
 * - Invalid/empty/out-of-range `start` or `end` returns 0: these values
 *   come from user-entered time sheets, so a silent 0 is the safer
 *   default (the broader form validation layer already rejects them).
 * - An invalid `config` throws `InvalidNightConfigError` because it
 *   originates from application code or tenant administration, where
 *   failing loudly catches bugs at boot/test time instead of silently
 *   zero-ing real driver hours.
 *
 * The function iterates minute by minute like the PHP V1 for clarity
 * and 1:1 parity with the reference algorithm; the engine runs offline
 * on small slots (a driver day rarely exceeds 24h), so O(n) in minutes
 * is negligible and easier to audit than a closed-form overlap formula.
 *
 * Invariant (checked in tests):
 *   computeNightMinutes(s, e) + dayMinutes = durationAcrossMidnight(s, e)
 */
export function computeNightMinutes(
  start: string,
  end: string,
  config: NightConfig = DEFAULT_NIGHT_CONFIG,
): number {
  assertNightConfig(config)

  const startMinutes = parseHhmmToMinutes(start)
  const endMinutes = parseHhmmToMinutes(end)
  if (startMinutes === null || endMinutes === null) {
    return 0
  }

  const total = durationAcrossMidnight(start, end)
  if (total === 0) {
    return 0
  }

  let nightMinutes = 0
  for (let offset = 0; offset < total; offset += 1) {
    const absoluteMinute = (startMinutes + offset) % MINUTES_PER_DAY
    const hour = Math.floor(absoluteMinute / MINUTES_PER_HOUR)
    if (isNightHour(hour, config)) {
      nightMinutes += 1
    }
  }
  return nightMinutes
}

function isNightHour(hour: number, config: NightConfig): boolean {
  return hour >= config.startHour || hour < config.endHour
}

function assertNightConfig(config: NightConfig): void {
  if (!isHourInRange(config.startHour)) {
    throw new InvalidNightConfigError(
      `startHour must be an integer in 0..23, got ${String(config.startHour)}`,
    )
  }
  if (!isHourInRange(config.endHour)) {
    throw new InvalidNightConfigError(
      `endHour must be an integer in 0..23, got ${String(config.endHour)}`,
    )
  }
  if (config.startHour <= config.endHour) {
    throw new InvalidNightConfigError(
      `night range must cross midnight (startHour > endHour), got ${String(config.startHour)} → ${String(config.endHour)}`,
    )
  }
}

function isHourInRange(hour: number): boolean {
  return Number.isInteger(hour) && hour >= 0 && hour <= MAX_HOUR
}
